import { useEffect, useMemo, useState } from 'react'
import type { CustomerOrder } from '../../customerOrdersApi'
import type { CustomerProfile } from '../../types'
import type { ShopLocale, ShopMessageKey } from '../../../i18n'
import {
  type BillingFormState,
  BILLING_PREFERENCES_STORAGE_PREFIX,
  readBooleanPreference,
} from './preferencesShared'
import {
  buildBillingDefaults,
  createDateFormatter,
  isBillingEmailInvalid,
  isTaxCodeInvalid,
  normalizeBillingEmail,
  normalizeTaxCode,
  resolveAssociatedAddress,
  resolveAssociatedName,
  resolveLatestPaymentDate,
  resolveLatestPaymentOrder,
} from './preferencesDerivedState'
import { saveBillingDataAction } from './preferencesSaveAction'

type UseCustomerPreferencesControllerParams = {
  profile: CustomerProfile
  orders: CustomerOrder[]
  locale: ShopLocale
  t: (key: ShopMessageKey, params?: Record<string, string | number>) => string
  onUpdateProfile: (payload: {
    firstName?: string
    lastName?: string
    taxCode?: string
    phone?: string
    email?: string
  }) => Promise<boolean>
}

/**
 * Owns billing/preferences state and save workflow for the customer preferences page.
 */
export const useCustomerPreferencesController = ({
  profile,
  orders,
  locale,
  t,
  onUpdateProfile,
}: UseCustomerPreferencesControllerParams) => {
  const billingDefaults = useMemo<BillingFormState>(
    () => buildBillingDefaults(profile),
    [profile],
  )

  const billingPreferenceStorageKey = useMemo(
    () => `${BILLING_PREFERENCES_STORAGE_PREFIX}:${(profile.email || 'guest').trim().toLowerCase() || 'guest'}`,
    [profile.email],
  )

  const [billingFormState, setBillingFormState] = useState<BillingFormState>(billingDefaults)
  const [useForNextOrders, setUseForNextOrders] = useState(true)
  const [billingError, setBillingError] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveState, setSaveState] = useState<'idle' | 'saved' | 'error'>('idle')
  const [touchedTaxCode, setTouchedTaxCode] = useState(false)
  const [touchedBillingEmail, setTouchedBillingEmail] = useState(false)

  useEffect(() => {
    setBillingFormState(billingDefaults)
  }, [billingDefaults])

  useEffect(() => {
    setUseForNextOrders(readBooleanPreference(billingPreferenceStorageKey, true))
  }, [billingPreferenceStorageKey])

  useEffect(() => {
    if (saveState === 'idle') return
    const timeoutId = window.setTimeout(() => setSaveState('idle'), 2400)
    return () => window.clearTimeout(timeoutId)
  }, [saveState])

  const associatedAddress = useMemo(
    () => resolveAssociatedAddress(profile),
    [profile],
  )

  const latestPaymentOrder = useMemo(() => resolveLatestPaymentOrder(orders), [orders])

  const dateFormatter = useMemo(() => createDateFormatter(locale), [locale])

  const latestPaymentDate = useMemo(() => {
    return resolveLatestPaymentDate(dateFormatter, latestPaymentOrder?.paidAt ?? null)
  }, [dateFormatter, latestPaymentOrder?.paidAt])

  const normalizedTaxCode = useMemo(() => normalizeTaxCode(billingFormState.taxCode), [billingFormState.taxCode])
  const normalizedBillingEmail = useMemo(
    () => normalizeBillingEmail(billingFormState.billingEmail),
    [billingFormState.billingEmail],
  )

  const taxCodeInvalid = isTaxCodeInvalid(normalizedTaxCode)
  const billingEmailInvalid = isBillingEmailInvalid(normalizedBillingEmail)
  const associatedName = useMemo(() => resolveAssociatedName(billingFormState), [billingFormState])

  const saveBillingData = async () => {
    await saveBillingDataAction({
      billingFormState,
      normalizedTaxCode,
      taxCodeInvalid,
      billingEmailInvalid,
      billingPreferenceStorageKey,
      useForNextOrders,
      setTouchedTaxCode,
      setTouchedBillingEmail,
      setBillingError,
      setSaveState,
      setSaving,
      onUpdateProfile,
      t,
    })
  }

  return {
    billingFormState,
    setBillingFormState,
    useForNextOrders,
    setUseForNextOrders,
    billingError,
    saving,
    saveState,
    touchedTaxCode,
    setTouchedTaxCode,
    touchedBillingEmail,
    setTouchedBillingEmail,
    taxCodeInvalid,
    billingEmailInvalid,
    associatedAddress,
    associatedName,
    latestPaymentOrder,
    latestPaymentDate,
    saveBillingData,
  }
}
